/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

import { FormMessageService } from '@gh/core-mls';
import { getAllErrors } from '@gh/core-ui';
import { first$ } from '@gh/rx/operators';

import { first, isEqual } from 'lodash';
import { Subscription } from 'rxjs';

import { GridCellTooltipDirective } from '../grid-cell-tooltip/grid-cell-tooltip.directive';

@Component({
  selector: 'gh-grid-cell-editor-container',
  template: require('./grid-cell-editor-container.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridCellEditorContainerComponent implements AfterViewInit, OnDestroy {
  @Input() control: AbstractControl;

  @Input()
  set noTooltip(noTooltip: any) {
    this.allowTooltip = !coerceBooleanProperty(noTooltip);
  }

  @ViewChild(GridCellTooltipDirective, { static: false }) tooltip: GridCellTooltipDirective;

  private allowTooltip: boolean = true;
  private _message: string;
  private invalid: boolean;
  private lastErrors: Maybe<ValidationErrors>;
  private statusSubscription?: Subscription;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private messageService: FormMessageService,
              private ngZone: NgZone) {
  }

  get message(): string {
    if (this.allowTooltip && this.control.invalid) {
      return this._message;
    } else {
      return '';
    }
  }

  ngAfterViewInit(): void {
    this.updateStatus();

    this.statusSubscription = this.control.statusChanges.subscribe(() => this.updateStatus());
  }

  ngOnDestroy(): void {
    const { statusSubscription } = this;

    if (statusSubscription) {
      statusSubscription.unsubscribe();
      this.statusSubscription = void 0;
    }
  }

  private updateStatus(): void {
    // update status only when invalid status is changed or list of errors has been changed
    if (!this.allowTooltip || this.invalid === this.control.invalid && !this.invalid) {
      return;
    }
    const newErrors = getAllErrors(this.control);
    if (this.invalid && isEqual(this.lastErrors, newErrors)) {
      return;
    }

    // this is necessary to avoid ExpressionChangedAfterItHasBeenCheckedError
    if (newErrors) {
      const key = first(Object.keys(newErrors))!; // tslint:disable-line:no-non-null-assertion
      this._message = this.messageService.getText(key, newErrors[key]);
    } else {
      this._message = '';
    }

    // this is necessary in order to force tooltip to update message property
    this.changeDetectorRef.markForCheck();

    // we wait until tooltip has applied new message
    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.invalid = this.control.invalid;
      this.lastErrors = newErrors;

      this.setTooltipVisible(this.invalid);
    });
  }

  private setTooltipVisible(visible: boolean): void {
    const { tooltip } = this;
    if (visible) {
      tooltip.show();
    } else {
      tooltip.forceHide();
    }
  }
}
